import React, {useEffect, useState, useRef, useCallback} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import i18n from '../i18n';
import './FrontPage.css';
import RoomCard from '../components/frontpage/RoomCard.js';
import GameModeSelector from '../components/frontpage/GameModeSelector.js';
import DrawingStrip from '../components/frontpage/DrawingStrip.js';
import LangSelector from '../components/common/LangSelector.js';
import NewsButton from '../components/common/NewsButton.js';
import {generateUsername} from '../utils/utils.js';
import {containsProfanity} from '../utils/profanity.js';
import {GAME_TYPES} from '../api';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

// Unambiguous characters: no 0/O/I/1 to avoid read-back errors.
const ID_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
function generateRoomId() {
    return Array.from({length: 6}, () => ID_CHARS[Math.floor(Math.random() * ID_CHARS.length)]).join('');
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const POLL_INTERVAL = 8000;

const isWatchMode = window.location.hostname === 'watch.suttu.io';

export default function FrontPage() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [connectionLost, setConnectionLost] = useState(false);
    const [lostRoomPath, setLostRoomPath] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [joinId, setJoinId] = useState('');
    const [gameMode, setGameMode] = useState('guess');
    const [createOpen, setCreateOpen] = useState(false);
    const [joinError, setJoinError] = useState('');
    // Guess-mode room settings
    const [drawTime, setDrawTime] = useState(150);
    const [drawTimeStr, setDrawTimeStr] = useState('150');
    const [minPlayers, setMinPlayers] = useState(1);
    const [minPlayersStr, setMinPlayersStr] = useState('1');
    const [everyoneDraws, setEveryoneDraws] = useState(false);
    const [createPrivate, setCreatePrivate] = useState(false);
    const [maxUsers, setMaxUsers] = useState(0);
    const [maxUsersStr, setMaxUsersStr] = useState('');
    const [selectedWordSets, setSelectedWordSets] = useState([]);
    const [wordSetsTree, setWordSetsTree] = useState({ files: [], folders: [] });
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [multiSelect, setMultiSelect] = useState(false);
    const [totalRounds, setTotalRounds] = useState(10);
    const [totalRoundsStr, setTotalRoundsStr] = useState('10');
    const [galleryPeriod, setGalleryPeriod] = useState('week');
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [customWords, setCustomWords] = useState([]);      // parsed user-uploaded words
    const [customFileName, setCustomFileName] = useState(''); // display name for UI
    const [customError, setCustomError] = useState('');
    const customFileInputRef = useRef(null);
    const [createUsername, setCreateUsername] = useState(() => localStorage.getItem('suttu_nickname') || generateUsername());
    const [isAutoName, setIsAutoName] = useState(() => !localStorage.getItem('suttu_nickname'));
    const bgRef = useRef(null);

    const handleMouseMove = useCallback((e) => {
        if (!bgRef.current) return;
        const mx = (e.clientX / window.innerWidth - 0.5) * 2;   // -1..1
        const my = (e.clientY / window.innerHeight - 0.5) * 2;
        bgRef.current.style.setProperty('--mx', mx);
        bgRef.current.style.setProperty('--my', my);
    }, []);

    useEffect(() => {
        if (location.state?.connectionLost) {
            setConnectionLost(true);
            setLostRoomPath(location.state.roomPath || null);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        let active = true;
        function fetchRooms() {
            fetch(API_BASE + '/api/rooms')
                .then(res => res.json())
                .then(data => { if (active) setRooms(data); })
                .catch(() => {});
        }
        fetchRooms();
        const interval = setInterval(fetchRooms, POLL_INTERVAL);
        return () => { active = false; clearInterval(interval); };
    }, []);

    useEffect(() => {
        const lang = i18n.language;
        fetch(API_BASE + '/api/wordsets?lang=' + encodeURIComponent(lang))
            .then(res => res.json())
            .then(data => {
                const tree = data.tree || data; // support new {tree, defaultWordSet} and old format
                const defaultWs = data.defaultWordSet || null;
                setWordSetsTree(tree || { files: [], folders: [] });
                setExpandedFolders(new Set());
                const allKeys = [];
                const collect = (node) => {
                    node.files.forEach(f => allKeys.push(f.key));
                    node.folders.forEach(f => collect(f));
                };
                collect(tree || { files: [], folders: [] });
                setSelectedWordSets(prev => {
                    const valid = prev.filter(k => allKeys.includes(k));
                    if (valid.length > 0) return valid;
                    if (defaultWs && allKeys.includes(defaultWs)) return [defaultWs];
                    return allKeys[0] ? [allKeys[0]] : [];
                });
            })
            .catch(() => {});
    }, [t]); // t changes when language changes

    const handleCustomFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setCustomError('');
        // Reject anything bigger than 1MB up-front — server caps word count but
        // we shouldn't even attempt to read huge files into memory on weak devices.
        if (file.size > 1024 * 1024) {
            setCustomError(t('frontPage.customWordsFileTooLarge') || 'File too large (max 1MB)');
            e.target.value = '';
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result || '');
            // Split on any line break; strip control chars; trim; filter empties/dupes.
            const seen = Object.create(null);
            const words = [];
            const lines = text.split(/\r?\n/);
            for (let i = 0; i < lines.length && words.length < 5000; i++) {
                // eslint-disable-next-line no-control-regex
                const cleaned = lines[i].replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim();
                if (!cleaned) continue;
                if (cleaned.length > 60) continue;
                if (seen[cleaned]) continue;
                seen[cleaned] = 1;
                words.push(cleaned);
            }
            if (words.length === 0) {
                setCustomError(t('frontPage.customWordsEmpty') || 'File has no valid words');
                setCustomWords([]);
                setCustomFileName('');
            } else {
                setCustomWords(words);
                setCustomFileName(file.name);
                setAdvancedOpen(true);
            }
        };
        reader.onerror = () => {
            setCustomError(t('frontPage.customWordsReadFailed') || 'Could not read file');
        };
        reader.readAsText(file);
        e.target.value = ''; // allow re-selecting the same file
    };

    const clearCustomWords = () => {
        setCustomWords([]);
        setCustomFileName('');
        setCustomError('');
    };

    const handleCreate = () => {
        let url = '/room/' + generateRoomId() + '?game=' + gameMode + '&lang=' + encodeURIComponent(i18n.language);
        if (gameMode === 'guess') {
            url += '&drawTime=' + Math.max(10, Math.min(300, drawTime || 150));
            url += '&minPlayers=' + Math.max(1, Math.min(20, minPlayers || 1));
            url += '&totalRounds=' + Math.max(1, Math.min(100, totalRounds || 10));
            if (everyoneDraws) url += '&everyoneDraws=1';
            if (selectedWordSets.length > 0) {
                selectedWordSets.forEach(ws => { url += '&wordSet=' + encodeURIComponent(ws); });
            }
            // Stash custom wordset in sessionStorage (too large for URL params).
            // RoomPage picks it up on initial mount and sends it over the socket.
            if (customWords.length > 0) {
                try {
                    const match = url.match(/\/room\/([A-Z0-9_-]+)/);
                    if (match) {
                        sessionStorage.setItem('suttu_custom_words_' + match[1], JSON.stringify({words: customWords, name: customFileName}));
                    }
                } catch (e) { /* ignore storage failures */ }
            }
        }
        if (maxUsers > 0) url += '&maxUsers=' + Math.min(100, maxUsers);
        if (createPrivate) url += '&private=1';
        if (createUsername.length >= 3) {
            if (containsProfanity(createUsername)) return;
            localStorage.setItem('suttu_nickname', createUsername);
            url += '&user=' + encodeURIComponent(createUsername);
        }
        setCreateOpen(false);
        navigate(url);
    };

    const handleJoin = () => {
        const sanitized = joinId.replace(/[^a-zA-Z0-9_-]/g, '').toUpperCase().substring(0, 6);
        if (!sanitized) return;
        setJoinError('');
        fetch(API_BASE + '/api/rooms/' + encodeURIComponent(sanitized))
            .then(res => {
                if (res.ok) {
                    navigate('/room/' + sanitized);
                } else {
                    setJoinError(t('frontPage.roomNotFound'));
                }
            })
            .catch(() => setJoinError(t('frontPage.roomNotFound')));
    };

    const handleJoinKeyPress = (e) => {
        if (e.key === 'Enter') handleJoin();
    };

    const handlePlayNow = () => {
        const lang = encodeURIComponent(i18n.language);
        fetch(API_BASE + '/api/quickjoin?lang=' + lang)
            .then(res => res.json())
            .then(data => {
                if (data.roomId) {
                    navigate('/room/' + data.roomId);
                } else {
                    // Create a new room with default settings
                    const ws = data.wordSet || 'General';
                    const url = '/room/' + generateRoomId() + '?game=guess&lang=' + lang
                        + '&drawTime=150&minPlayers=1&totalRounds=10&wordSet=' + encodeURIComponent(ws);
                    navigate(url);
                }
            })
            .catch(() => {
                // Fallback: create a new room
                const url = '/room/' + generateRoomId() + '?game=guess&lang=' + encodeURIComponent(i18n.language)
                    + '&drawTime=150&minPlayers=1&totalRounds=10&wordSet=' + encodeURIComponent('General');
                navigate(url);
            });
    };

    const currentLang = i18n.language;
    const filteredRooms = rooms.filter(r => {
        if ((r.gameType || 'draw') !== gameMode) return false;
        if (gameMode !== 'draw' && (r.lang || 'en') !== currentLang) return false;
        return true;
    });
    const activeRooms = filteredRooms.filter(r => r.userCount > 0);
    // Cap empty/recent rooms — they can accumulate; the strip already scrolls,
    // but rendering hundreds of cards bloats the DOM for no real benefit.
    const RECENT_ROOMS_LIMIT = 30;
    const emptyRooms = filteredRooms.filter(r => r.userCount === 0).slice(0, RECENT_ROOMS_LIMIT);
    const totalEmptyRooms = filteredRooms.filter(r => r.userCount === 0).length;

    // ── Watch mode (watch.suttu.io) — minimal room-code entry for TV spectators ──
    const [watchCode, setWatchCode] = useState('');
    const [watchError, setWatchError] = useState('');
    const watchInputRef = useRef(null);

    useEffect(() => {
        if (isWatchMode && watchInputRef.current) watchInputRef.current.focus();
    }, []);

    const handleWatchJoin = () => {
        var code = watchCode.replace(/[^a-zA-Z0-9_-]/g, '').toUpperCase().substring(0, 6);
        if (!code) return;
        setWatchError('');
        fetch(API_BASE + '/api/rooms/' + encodeURIComponent(code))
            .then(function (res) {
                if (res.ok) {
                    var name = 'TV-' + generateUsername();
                    navigate('/room/' + code + '?spectator=1&user=' + encodeURIComponent(name));
                } else {
                    setWatchError(t('frontPage.roomNotFound', 'Room not found'));
                }
            })
            .catch(function () { setWatchError(t('frontPage.roomNotFound', 'Room not found')); });
    };

    if (isWatchMode) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: '100vh', background: '#1a1a2e', color: '#eee', fontFamily: 'monospace',
                padding: 24,
            }}>
                <h1 style={{fontSize: 32, fontWeight: 700, marginBottom: 8, letterSpacing: 2}}>suttu.io</h1>
                <p style={{fontSize: 16, color: '#aab', marginBottom: 32}}>
                    {t('frontPage.watchSubtitle', 'Enter room code to spectate')}
                </p>
                <input
                    ref={watchInputRef}
                    type="text"
                    value={watchCode}
                    onChange={function (e) { setWatchCode(e.target.value.toUpperCase()); setWatchError(''); }}
                    onKeyDown={function (e) { if (e.key === 'Enter') handleWatchJoin(); }}
                    placeholder={t('frontPage.roomCodePlaceholder', 'ROOM CODE')}
                    maxLength={6}
                    autoFocus
                    style={{
                        fontSize: 36, fontFamily: 'monospace', fontWeight: 700,
                        textAlign: 'center', letterSpacing: 8,
                        width: 280, padding: '16px 20px',
                        background: '#2a2a4e', color: '#fff', border: '2px solid #556',
                        borderRadius: 12, outline: 'none',
                        caretColor: '#77f',
                    }}
                />
                {watchError && (
                    <p style={{color: '#ff6b6b', marginTop: 12, fontSize: 14}}>{watchError}</p>
                )}
                <button
                    onClick={handleWatchJoin}
                    style={{
                        marginTop: 24, fontSize: 20, fontWeight: 700, fontFamily: 'monospace',
                        padding: '14px 48px', background: '#3a3a6e', color: '#eef',
                        border: '2px solid #556', borderRadius: 10, cursor: 'pointer',
                    }}
                >
                    {t('frontPage.watchJoin', 'Watch')}
                </button>
            </div>
        );
    }

    return (
        <div className="FrontPage" onMouseMove={handleMouseMove}>
            <div className="FrontPage-bg" ref={bgRef}>
                <div className="FrontPage-bg-doodles" style={{backgroundImage: 'url(' + process.env.PUBLIC_URL + '/doodles.png)'}}></div>
                <div className="FrontPage-bg-grid"></div>
            </div>
            <div className="FrontPage-top-menu">
                <NewsButton />
                <LangSelector />
            </div>
            <div className="FrontPage-header">
                {connectionLost && (
                    <div style={{
                        background: '#b94040', color: '#fff', padding: '10px 20px', borderRadius: 8,
                        marginBottom: 16, fontSize: 14, display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', gap: 12,
                    }}>
                        <span>{t('frontPage.connectionLost')}</span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {lostRoomPath && (
                                <button
                                    onClick={() => navigate(lostRoomPath)}
                                    style={{
                                        background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.5)',
                                        color: '#fff', borderRadius: 4, padding: '2px 12px', cursor: 'pointer', fontSize: 13,
                                        fontWeight: 600,
                                    }}
                                >{t('frontPage.rejoin')}</button>
                            )}
                            <button
                                onClick={() => setConnectionLost(false)}
                                style={{
                                    background: 'transparent', border: '1px solid rgba(255,255,255,0.4)',
                                    color: '#fff', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 13,
                                }}
                            >✕</button>
                        </div>
                    </div>
                )}
                <div className="FrontPage-title-row">
                    <h1>{t('frontPage.title')}</h1>
                    <span className="FrontPage-io-suffix">.io</span>
                    <span className="FrontPage-alpha-badge">ALPHA</span>
                </div>

                <div className="FrontPage-mode-wrap">
                    <GameModeSelector value={gameMode} onChange={setGameMode} />
                    <p className="FrontPage-mode-desc">
                        {t((GAME_TYPES.find(m => m.key === gameMode) || GAME_TYPES[0]).descKey)}
                    </p>
                </div>

                <div className="FrontPage-actions">
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center'}}>
                        {gameMode === 'guess' && (
                            <Button variant="contained" color="primary"
                                    onClick={handlePlayNow}
                                    style={{textTransform: 'none', fontWeight: 600, fontSize: 16, padding: '8px 28px'}}>
                                {t('frontPage.playNow')}
                            </Button>
                        )}
                        <Button variant={gameMode === 'guess' ? 'outlined' : 'contained'}
                                color={gameMode === 'guess' ? 'inherit' : 'primary'}
                                onClick={() => setCreateOpen(true)}
                                style={{textTransform: 'none', fontWeight: 500, fontSize: 14,
                                        ...(gameMode === 'guess' ? {color: '#bbc', borderColor: '#556'} : {})}}>
                            {gameMode === 'guess' ? t('frontPage.customRoom') : t('frontPage.createRoom')}
                        </Button>
                    </div>
                    <div className="FrontPage-or"><span>{t('frontPage.or')}</span></div>
                    <div className="FrontPage-join">
                        <input
                            placeholder={t('frontPage.privateRoomId')}
                            value={joinId}
                            onChange={e => setJoinId(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').toUpperCase().substring(0, 6))}
                            onKeyPress={handleJoinKeyPress}
                        />
                        <Button variant="outlined" onClick={handleJoin}
                                disabled={!joinId}
                                style={{color: '#bbc', borderColor: '#555', textTransform: 'none'}}>
                            {t('frontPage.join')}
                        </Button>
                        {joinError && <span style={{color: '#f66', fontSize: 13}}>{joinError}</span>}
                    </div>
                </div>

                <Dialog open={createOpen} onClose={() => setCreateOpen(false)}
                        fullWidth maxWidth="sm"
                        PaperProps={{style: {background: '#23233a', color: '#eee', borderRadius: 12, minWidth: 320, maxHeight: 'calc(100vh - 20px)', margin: 10, display: 'flex', flexDirection: 'column'}}}
                        disableScrollLock>
                    <DialogTitle style={{fontWeight: 600, flexShrink: 0}}>{t('frontPage.roomSettings')}</DialogTitle>
                    <DialogContent style={{display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 8, overflowY: 'auto', flex: 1}}>
                        {gameMode === 'guess' && (<>
                        {customWords.length > 0 && (
                            <div style={{fontSize: 12, color: '#aad', background: 'rgba(85,68,170,0.15)',
                                         border: '1px solid #554488', padding: '6px 10px', borderRadius: 4}}>
                                {t('frontPage.customWordsetActive') || 'Using your custom wordset. Preset word sets are disabled.'}
                            </div>
                        )}
                        <div style={{position: 'relative', opacity: customWords.length > 0 ? 0.4 : 1, pointerEvents: customWords.length > 0 ? 'none' : 'auto'}}>
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4}}>
                                <label style={{fontSize: 14, color: '#bbc'}}>{t('frontPage.wordSets')}</label>
                                <span
                                    title={multiSelect ? t('frontPage.singleSelect') : t('frontPage.multiSelect')}
                                    onClick={() => {
                                        setMultiSelect(prev => {
                                            if (!prev) return true; // switching to multi — keep current selection
                                            // switching to single — keep only the first
                                            setSelectedWordSets(sel => sel.length > 0 ? [sel[0]] : sel);
                                            return false;
                                        });
                                    }}
                                    style={{
                                        cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '2px 5px',
                                        borderRadius: 4, userSelect: 'none',
                                        background: multiSelect ? '#5544aa' : 'transparent',
                                        color: multiSelect ? '#eef' : '#889',
                                        border: '1px solid ' + (multiSelect ? '#7766cc' : '#556'),
                                        transition: 'all 0.15s',
                                    }}
                                >☰</span>
                            </div>
                            {(() => {
                                const toggleFolder = (path) => setExpandedFolders(prev => {
                                    const next = new Set(prev);
                                    if (next.has(path)) next.delete(path); else next.add(path);
                                    return next;
                                });
                                const collectKeys = (node) => {
                                    const keys = [];
                                    node.files.forEach(f => keys.push(f.key));
                                    node.folders.forEach(sub => keys.push(...collectKeys(sub)));
                                    return keys;
                                };
                                const handleFileClick = (key) => {
                                    if (multiSelect) {
                                        setSelectedWordSets(prev =>
                                            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                                        );
                                    } else {
                                        setSelectedWordSets([key]);
                                    }
                                };
                                const renderFile = (f, indent) => {
                                    const isSelected = selectedWordSets.includes(f.key);
                                    return (
                                        <div
                                            key={f.key}
                                            onClick={() => handleFileClick(f.key)}
                                            style={{
                                                padding: '6px 10px 6px ' + (10 + indent * 16) + 'px',
                                                cursor: 'pointer', fontSize: 13,
                                                background: isSelected ? '#5544aa' : 'transparent',
                                                color: isSelected ? '#fff' : '#dde',
                                                borderRadius: 4,
                                                display: 'flex', alignItems: 'center', gap: 6,
                                            }}
                                        >
                                            {multiSelect && <span style={{fontSize: 14, lineHeight: 1, flexShrink: 0}}>{isSelected ? '☑' : '☐'}</span>}
                                            {f.name}
                                        </div>
                                    );
                                };
                                const renderFolder = (folder, depth) => {
                                    const path = depth + '/' + folder.name;
                                    const isOpen = expandedFolders.has(path);
                                    const childKeys = collectKeys(folder);
                                    const selCount = childKeys.filter(k => selectedWordSets.includes(k)).length;
                                    return (
                                        <div key={path}>
                                            <div
                                                onClick={() => toggleFolder(path)}
                                                style={{
                                                    padding: '6px 10px 6px ' + (10 + depth * 16) + 'px',
                                                    cursor: 'pointer', fontSize: 13,
                                                    color: '#ccf', display: 'flex', alignItems: 'center', gap: 5,
                                                    borderRadius: 4, fontWeight: 600,
                                                    background: 'rgba(80,60,160,0.1)',
                                                }}
                                            >
                                                <span style={{
                                                    fontSize: 9, display: 'inline-block',
                                                    transition: 'transform 0.15s',
                                                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                }}>▶</span>
                                                {folder.name}
                                                {selCount > 0 && <span style={{fontSize: 11, color: '#99f', fontWeight: 400, marginLeft: 'auto'}}>({selCount})</span>}
                                            </div>
                                            {isOpen && (
                                                <div>
                                                    {folder.files.map(f => renderFile(f, depth + 1))}
                                                    {folder.folders.map(sub => renderFolder(sub, depth + 1))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                };
                                return (
                                    <div style={{
                                        border: '1px solid #555', borderRadius: 6, background: '#2a2a3e',
                                        maxHeight: 220, overflowY: 'auto', padding: 2,
                                    }}>
                                        {wordSetsTree.files.map(f => renderFile(f, 0))}
                                        {wordSetsTree.folders.map(folder => renderFolder(folder, 0))}
                                    </div>
                                );
                            })()}
                            {multiSelect && selectedWordSets.length > 0 && (
                                <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6}}>
                                    {selectedWordSets.map(ws => {
                                        const name = ws.lastIndexOf('/') >= 0 ? ws.slice(ws.lastIndexOf('/') + 1) : ws;
                                        return (
                                            <span
                                                key={ws}
                                                onClick={() => setSelectedWordSets(prev => prev.filter(k => k !== ws))}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                    padding: '2px 8px 2px 10px', borderRadius: 12,
                                                    background: '#5544aa', color: '#eef', fontSize: 12,
                                                    cursor: 'pointer', userSelect: 'none',
                                                    transition: 'background 0.15s',
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#6655bb'}
                                                onMouseLeave={e => e.currentTarget.style.background = '#5544aa'}
                                            >
                                                {name}
                                                <span style={{fontSize: 14, opacity: 0.7, lineHeight: 1}}>×</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div
                            onClick={() => setAdvancedOpen(prev => !prev)}
                            style={{
                                cursor: 'pointer', fontSize: 13, color: '#99a',
                                display: 'flex', alignItems: 'center', gap: 5,
                                userSelect: 'none', padding: '2px 0',
                            }}
                        >
                            <span style={{
                                fontSize: 9, display: 'inline-block',
                                transition: 'transform 0.15s',
                                transform: advancedOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                            }}>▶</span>
                            {t('frontPage.advancedSettings')}
                        </div>
                        {advancedOpen && (<>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 4, padding: 8, border: '1px dashed #555', borderRadius: 6, background: 'rgba(85,68,170,0.08)'}}>
                            <label style={{fontSize: 14, color: '#bbc', fontWeight: 600}}>
                                {t('frontPage.customWordset') || 'Custom wordset'}
                            </label>
                            <div style={{fontSize: 11, color: '#889', marginBottom: 2}}>
                                {t('frontPage.customWordsetHint') || 'Overrides the word sets above.'}
                            </div>
                            <input
                                ref={customFileInputRef}
                                type="file"
                                accept=".txt,text/plain"
                                onChange={handleCustomFileChange}
                                style={{display: 'none'}}
                            />
                            {customFileName ? (
                                <div style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#bbc'}}>
                                    <span style={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                        ✓ {customFileName} ({customWords.length})
                                    </span>
                                    <button
                                        type="button" onClick={clearCustomWords}
                                        style={{background: 'transparent', border: '1px solid #555', color: '#bbc',
                                                padding: '2px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12}}
                                    >×</button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => customFileInputRef.current && customFileInputRef.current.click()}
                                    style={{background: '#2a2a3e', border: '1px solid #555', color: '#bbc',
                                            padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
                                            textAlign: 'left'}}
                                >
                                    {t('frontPage.customWordsetUpload') || 'Upload .txt file…'}
                                </button>
                            )}
                            {customError && (
                                <div style={{fontSize: 12, color: '#e67'}}>{customError}</div>
                            )}
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <label style={{fontSize: 14, color: '#bbc', minWidth: 110}}>{t('frontPage.rounds')}</label>
                            <input
                                type="number" min="1" max="100" value={totalRoundsStr}
                                onChange={e => setTotalRoundsStr(e.target.value)}
                                onBlur={e => {
                                    const v = Math.max(1, Math.min(100, Number(e.target.value) || 10));
                                    setTotalRounds(v);
                                    setTotalRoundsStr(String(v));
                                }}
                                style={{width: 60, padding: '4px 6px', borderRadius: 4, border: '1px solid #555',
                                        background: '#2a2a3e', color: '#eee', fontSize: 14, textAlign: 'center'}}
                            />
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <label style={{fontSize: 14, color: '#bbc', minWidth: 110}}>{t('frontPage.drawTime')}</label>
                            <input
                                type="number" min="10" max="300" value={drawTimeStr}
                                onChange={e => setDrawTimeStr(e.target.value)}
                                onBlur={e => {
                                    const v = Math.max(10, Math.min(300, Number(e.target.value) || 60));
                                    setDrawTime(v);
                                    setDrawTimeStr(String(v));
                                }}
                                style={{width: 60, padding: '4px 6px', borderRadius: 4, border: '1px solid #555',
                                        background: '#2a2a3e', color: '#eee', fontSize: 14, textAlign: 'center'}}
                            />
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <label style={{fontSize: 14, color: '#bbc', minWidth: 110}}>{t('frontPage.minPlayers')}</label>
                            <input
                                type="number" min="1" max="20" value={minPlayersStr}
                                onChange={e => setMinPlayersStr(e.target.value)}
                                onBlur={e => {
                                    const v = Math.max(1, Math.min(20, Number(e.target.value) || 1));
                                    setMinPlayers(v);
                                    setMinPlayersStr(String(v));
                                }}
                                style={{width: 60, padding: '4px 6px', borderRadius: 4, border: '1px solid #555',
                                        background: '#2a2a3e', color: '#eee', fontSize: 14, textAlign: 'center'}}
                            />
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <label style={{fontSize: 14, color: '#bbc', minWidth: 110}}>{t('frontPage.maxUsers')}</label>
                            <input
                                type="number" min="0" max="100" value={maxUsersStr}
                                placeholder={t('frontPage.noLimit')}
                                onChange={e => setMaxUsersStr(e.target.value)}
                                onBlur={e => {
                                    const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                    setMaxUsers(v);
                                    setMaxUsersStr(v > 0 ? String(v) : '');
                                }}
                                style={{width: 80, padding: '4px 6px', borderRadius: 4, border: '1px solid #555',
                                        background: '#2a2a3e', color: '#eee', fontSize: 14, textAlign: 'center'}}
                            />
                        </div>
                        </>)}
                        </>)}
                        {gameMode !== 'guess' && (<>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <label style={{fontSize: 14, color: '#bbc', minWidth: 110}}>{t('frontPage.maxUsers')}</label>
                            <input
                                type="number" min="0" max="100" value={maxUsersStr}
                                placeholder={t('frontPage.noLimit')}
                                onChange={e => setMaxUsersStr(e.target.value)}
                                onBlur={e => {
                                    const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                    setMaxUsers(v);
                                    setMaxUsersStr(v > 0 ? String(v) : '');
                                }}
                                style={{width: 80, padding: '4px 6px', borderRadius: 4, border: '1px solid #555',
                                        background: '#2a2a3e', color: '#eee', fontSize: 14, textAlign: 'center'}}
                            />
                        </div>
                        </>)}
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <label style={{fontSize: 14, color: '#bbc', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6}}>
                                <input type="checkbox" checked={createPrivate}
                                       onChange={e => setCreatePrivate(e.target.checked)} />
                                {t('frontPage.private')}
                            </label>
                        </div>
                        <div style={{marginTop: 4}}>
                            <label style={{fontSize: 14, color: '#bbc'}}>{t('roomPage.namePlaceholder')}</label>
                            <input
                                type="text" maxLength={15}
                                value={createUsername}
                                onChange={e => setCreateUsername(e.target.value.replace(/[^\w\s\-]/g, '').substring(0, 15))}
                                onFocus={() => { if (isAutoName) { setCreateUsername(''); setIsAutoName(false); } }}
                                style={{width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 6, border: '1px solid #555',
                                        background: '#2a2a3e', color: '#eee', fontSize: 14, boxSizing: 'border-box'}}
                            />
                        </div>
                    </DialogContent>
                    <DialogActions style={{padding: '8px 20px 16px', flexShrink: 0}}>
                        <Button onClick={() => setCreateOpen(false)} style={{color: '#899', textTransform: 'none'}}>{t('frontPage.cancel')}</Button>
                        <Button variant="contained" onClick={handleCreate}
                            disabled={(gameMode === 'guess' && selectedWordSets.length === 0 && customWords.length === 0) || createUsername.length < 3}
                            style={{textTransform: 'none'}}>{t('frontPage.create')}</Button>
                    </DialogActions>
                </Dialog>
            </div>

            {activeRooms.length > 0 && (
                <div className="FrontPage-section">
                    <div className="FrontPage-section-header">
                        <h2>{t('frontPage.activeRooms')}</h2>
                        <span className="FrontPage-section-count">{activeRooms.length}</span>
                    </div>
                    <div className="FrontPage-grid">
                        {activeRooms.map(r => (
                            <RoomCard key={r.id} id={r.id} userCount={r.userCount} maxUsers={r.maxUsers} thumbnail={r.thumbnail} gameType={r.gameType} wordSets={r.wordSets} customWordsName={r.customWordsName} />
                        ))}
                    </div>
                </div>
            )}

            {emptyRooms.length > 0 && (
                <div className="FrontPage-section">
                    <div className="FrontPage-section-header">
                        <h2>{t('frontPage.recentRooms')}</h2>
                        <span className="FrontPage-section-count">
                            {totalEmptyRooms > emptyRooms.length ? `${emptyRooms.length}/${totalEmptyRooms}` : emptyRooms.length}
                        </span>
                    </div>
                    <div className="FrontPage-strip">
                        {emptyRooms.map(r => (
                            <RoomCard key={r.id} id={r.id} userCount={r.userCount} maxUsers={r.maxUsers} thumbnail={r.thumbnail} gameType={r.gameType} wordSets={r.wordSets} customWordsName={r.customWordsName} />
                        ))}
                    </div>
                </div>
            )}

            {filteredRooms.length === 0 && (
                <div className="FrontPage-section">
                    <div className="FrontPage-empty">
                        {t('frontPage.noRooms')}
                    </div>
                </div>
            )}

            {/* Gallery highlights — only on draw tab */}
            {gameMode === 'draw' && <div className="FrontPage-section FrontPage-gallery">
                <div className="FrontPage-section-header">
                    <h2>{t('frontPage.galleryTitle', 'Gallery')}</h2>
                </div>
                <div className="FrontPage-gallery-header">
                    <h3 className="FrontPage-gallery-subtitle">{t('frontPage.communityFavorites', 'Community Favorites')}</h3>
                    <div className="FrontPage-period-chips">
                        {['today', 'week', 'month', 'all'].map(p => (
                            <button
                                key={p}
                                className={`FrontPage-chip${galleryPeriod === p ? ' active' : ''}`}
                                onClick={() => setGalleryPeriod(p)}
                            >
                                {t(`frontPage.period.${p}`, p === 'all' ? 'All Time' : p === 'month' ? 'This Month' : p === 'week' ? 'This Week' : 'Today')}
                            </button>
                        ))}
                    </div>
                </div>
                <DrawingStrip
                    title=""
                    icon="⭐"
                    sort="top"
                    period={galleryPeriod}
                />
                <DrawingStrip
                    title={t('frontPage.freshStrokes', 'Fresh off the Canvas')}
                    sort="recent"
                    period="all"
                />
            </div>}

            <a
                href="https://tally.so/r/ob74DV"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    position: 'absolute', bottom: 10, left: 14,
                    fontSize: 12, color: '#8899bb', textDecoration: 'none',
                    background: 'rgba(20,18,40,0.6)', padding: '4px 14px', borderRadius: 12,
                    border: '1px solid rgba(100,110,140,0.25)',
                    zIndex: 2, letterSpacing: 0.3,
                    transition: 'color 0.2s, border-color 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#aac'; e.currentTarget.style.borderColor = 'rgba(140,150,200,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#8899bb'; e.currentTarget.style.borderColor = 'rgba(100,110,140,0.25)'; }}
            >
                💬 Give feedback
            </a>

            <div style={{
                position: 'absolute', bottom: 10, right: 14,
                fontSize: 11, color: '#445',
                userSelect: 'none', pointerEvents: 'none',
                zIndex: 2,
            }}>
                {process.env.REACT_APP_GIT_HASH ? ` · ${process.env.REACT_APP_GIT_HASH}` : ''}
            </div>
        </div>
    );
}
